import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatMonth } from '../../lib/format';
import { Button } from './Button';

/** Shift a 'YYYY-MM' key by whole months. */
export function shiftMonthKey(key: string, delta: number): string {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Today's month as a 'YYYY-MM' key, in the viewer's local calendar. */
export function currentMonthKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export interface MonthPickerProps {
  /** Selected month, 'YYYY-MM'. */
  month: string;
  onChange: (month: string) => void;
  /** Earliest selectable month, 'YYYY-MM'. Unbounded when omitted. */
  min?: string;
  /** Latest selectable month, 'YYYY-MM'. Defaults to the current month. */
  max?: string;
  /** Label of the shortcut back to `max` (default "This month"). */
  resetLabel?: string;
  className?: string;
}

/**
 * The one month stepper. Every screen that reports "a month at a time"
 * (dashboard, team, support …) uses this so the control, the shortcut back to
 * today, and — crucially — the month LABEL are identical everywhere. It renders
 * `formatMonth` ("Aug 2026"): one screen saying "August 2026" while its
 * neighbour says "Aug 2026" is how an app stops feeling like one product.
 */
export function MonthPicker({
  month,
  onChange,
  min,
  max,
  resetLabel = 'This month',
  className,
}: MonthPickerProps) {
  const latest = max ?? currentMonthKey();
  const canPrev = !min || shiftMonthKey(month, -1) >= min;
  const canNext = month < latest;
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-lg border border-grey-200 bg-white p-0.5',
        className,
      )}
      role="group"
      aria-label="Month"
    >
      <button
        type="button"
        className="btn-icon text-grey-500 hover:text-secondary disabled:opacity-30"
        onClick={() => onChange(shiftMonthKey(month, -1))}
        disabled={!canPrev}
        aria-label="Previous month"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </button>
      <span className="num min-w-[6.5rem] text-center text-body-sm font-semibold text-secondary">
        {formatMonth(`${month}-01`)}
      </span>
      <button
        type="button"
        className="btn-icon text-grey-500 hover:text-secondary disabled:opacity-30"
        onClick={() => onChange(shiftMonthKey(month, 1))}
        disabled={!canNext}
        aria-label="Next month"
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
      {month !== latest && (
        <Button variant="ghost" size="sm" onClick={() => onChange(latest)}>
          {resetLabel}
        </Button>
      )}
    </div>
  );
}
